import React, { useEffect, useState } from "react";
import { View, StyleSheet, TextInput } from "react-native";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import { ProjectRubricScoreInput, ProjectSubmissionRubricScore } from "@axio/graphql/generated/graphql";

interface ProjectGradingSectionProps {
  grade: number | null | undefined;
  feedback: string;
  onGradeChange: (newGrade: number | null) => void;
  onFeedbackChange: (newFeedback: string) => void;
  assessmentCriteria?: string[] | null | undefined;
  onRubricScoresChange?: (rubricScores: ProjectRubricScoreInput[]) => void;
  existingRubricScores?: Pick<ProjectSubmissionRubricScore, "criterionId" | "criterionName" | "earnedPoints">[] | null;
}

type RubricRow = {
  id: string;
  name: string;
  maxPoints: number;
  earnedPoints: string;
};

const buildCriteria = (names: string[]): RubricRow[] => {
  const base = Math.floor(100 / names.length);
  const remainder = 100 - base * names.length;
  return names.map((name, i) => ({
    id: String(i + 1),
    name,
    maxPoints: base + (i === names.length - 1 ? remainder : 0),
    earnedPoints: "",
  }));
};

const ProjectGradingSection: React.FC<ProjectGradingSectionProps> = ({
  grade,
  feedback,
  onGradeChange,
  onFeedbackChange,
  assessmentCriteria,
  onRubricScoresChange,
  existingRubricScores,
}) => {
  const initialCriteria = assessmentCriteria?.length ? buildCriteria(assessmentCriteria) : [];
  const [rubricCriteria, setRubricCriteria] = useState<RubricRow[]>(initialCriteria);

  const MAX_RUBRIC_TOTAL = rubricCriteria.reduce((sum, c) => sum + c.maxPoints, 0);

  const rubricEarnedTotal = rubricCriteria.reduce((sum, c) => {
    const val = parseFloat(c.earnedPoints);
    return sum + (isNaN(val) ? 0 : Math.min(val, c.maxPoints));
  }, 0);

  const hasAnyRubricInput = rubricCriteria.some((c) => c.earnedPoints !== "");
  const gradeFromRubric = MAX_RUBRIC_TOTAL > 0 ? Math.round((rubricEarnedTotal / MAX_RUBRIC_TOTAL) * 100) : 0;

  useEffect(() => {
    if (hasAnyRubricInput) {
      onGradeChange(gradeFromRubric);
    }
  }, [gradeFromRubric, hasAnyRubricInput]);

  useEffect(() => {
    if (!assessmentCriteria?.length) return;
    setRubricCriteria((prev) => {
      const newCriteria = buildCriteria(assessmentCriteria);
      return newCriteria.map((c, i) => ({
        ...c,
        earnedPoints: prev[i]?.earnedPoints ?? "",
      }));
    });
  }, [assessmentCriteria]);

  useEffect(() => {
    if (hasAnyRubricInput && onRubricScoresChange) {
      const scores: ProjectRubricScoreInput[] = rubricCriteria
        .filter((c) => c.earnedPoints !== "")
        .map((c) => ({
          criterionId: c.id,
          criterionName: c.name,
          earnedPoints: parseFloat(c.earnedPoints),
          maxPoints: c.maxPoints,
        }));
      onRubricScoresChange(scores);
    }
  }, [rubricCriteria, hasAnyRubricInput]);

  useEffect(() => {
    if (!existingRubricScores || existingRubricScores.length === 0) return;
    setRubricCriteria((prev) =>
      prev.map((c) => {
        const saved = existingRubricScores.find((s) => s.criterionId === c.id);
        return saved ? { ...c, earnedPoints: String(saved.earnedPoints) } : c;
      })
    );
  }, [existingRubricScores]);

  return (
    <View style={styles.gradingSection}>
      {assessmentCriteria?.length || hasAnyRubricInput ? (
        <View style={styles.rubricContainer}>
          <View style={styles.rubricHeaderRow}>
            <CustomText
              text="Criterion"
              size="s"
              weight="bold"
              style={[styles.label, styles.rubricCriterionCell]}
              useTranslationText={true}
            />
            <CustomText
              text="Pts Earned"
              size="s"
              weight="bold"
              style={[styles.label, styles.rubricScoreCell]}
              useTranslationText={true}
            />
            <CustomText
              text="Max Pts"
              size="s"
              weight="bold"
              style={[styles.label, styles.rubricMaxCell]}
              useTranslationText={true}
            />
          </View>
          {rubricCriteria.map((criterion) => (
            <View key={criterion.id} style={styles.rubricRow}>
              <CustomText
                text={criterion.name}
                size="s"
                style={[styles.rubricCriterionCell, styles.rubricCriterionText]}
                useTranslationText={false}
              />
              <View style={styles.rubricScoreCell}>
                <CustomTextInput
                  keyboardType="numeric"
                  value={criterion.earnedPoints}
                  onChangeText={(val) => {
                    if (val !== "") {
                      const num = parseFloat(val);
                      if (isNaN(num) || num < 0 || num > criterion.maxPoints) return;
                    }
                    setRubricCriteria((prev) =>
                      prev.map((c) => (c.id === criterion.id ? { ...c, earnedPoints: val } : c))
                    );
                  }}
                  placeholder="—"
                  style={styles.rubricInput}
                />
              </View>
              <CustomText
                text={`/ ${criterion.maxPoints}`}
                size="s"
                style={styles.rubricMaxCell}
                useTranslationText={false}
              />
            </View>
          ))}
          <View style={styles.rubricTotalRow}>
            <CustomText
              text="Total"
              size="s"
              weight="bold"
              style={styles.rubricCriterionCell}
              useTranslationText={true}
            />
            <CustomText
              text={hasAnyRubricInput ? String(rubricEarnedTotal) : "—"}
              size="s"
              weight="bold"
              style={styles.rubricScoreCell}
              useTranslationText={false}
            />
            <CustomText
              text={`/ ${MAX_RUBRIC_TOTAL}`}
              size="s"
              style={styles.rubricMaxCell}
              useTranslationText={false}
            />
          </View>
        </View>
      ) : (
        <View style={styles.gradeInputContainer}>
          <CustomText text="Grade" size="s" weight="bold" style={styles.label} />
          <View style={styles.gradeInputBorder}>
            <TextInput
              keyboardType="numeric"
              value={grade == null ? "" : grade.toString()}
              onChangeText={(value) => {
                const num = Number(value);
                if (value !== "" && isNaN(num)) return;
                onGradeChange(value === "" ? null : Math.max(0, Math.min(100, num)));
              }}
              placeholder="Enter grade"
              placeholderTextColor={colors.grey[400]}
              style={styles.gradeInput}
            />
            <CustomText text="/ 100" size="s" style={styles.gradeSuffix} useTranslationText={false} />
          </View>
          <CustomText text="Enter a grade from 0 to 100" size="xs" style={styles.gradeHelp} />
        </View>
      )}

      <View style={styles.feedbackContainer}>
        <CustomText text="Feedback for Student (Optional)" size="s" weight="bold" style={styles.label} />
        <CustomTextInput
          value={feedback}
          onChangeText={onFeedbackChange}
          placeholder="Provide feedback on the student's work..."
          multiline
          numberOfLines={8}
          maxLength={10000}
          style={styles.feedbackTextarea}
        />
        <CustomText
          text="character_count"
          size="xs"
          style={styles.feedbackCharCount}
          interpolation={{ value: feedback?.length || 0 }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  gradingSection: {
    borderTopWidth: 1,
    borderColor: colors.grey[200],
    paddingTop: spacing.md,
    marginBottom: spacing.xl,
  },
  feedbackContainer: {
    flex: 1,
  },
  feedbackTextarea: {
    marginTop: spacing.xs,
    minHeight: 200,
    textAlignVertical: "top",
  },
  feedbackCharCount: {
    color: colors.grey[500],
    textAlign: "right",
    marginTop: spacing.xs,
  },
  gradeInputContainer: {
    marginBottom: spacing.md,
  },
  gradeInputBorder: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: 10,
    backgroundColor: colors.white,
    marginTop: 6,
  },
  gradeInput: {
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
    // @ts-expect-error - web only
    outlineWidth: 0,
  },
  gradeSuffix: {
    color: colors.grey[500],
    marginLeft: spacing.xs,
  },
  gradeHelp: {
    color: colors.grey[500],
    marginTop: 6,
  },
  label: {
    color: colors.grey[700],
    fontWeight: "500",
  },
  rubricContainer: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
    overflow: "hidden",
    marginBottom: spacing.md,
  },
  rubricHeaderRow: {
    flexDirection: "row",
    backgroundColor: colors.grey[100],
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    gap: spacing.xs,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    gap: spacing.xs,
  },
  rubricTotalRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    borderTopWidth: 2,
    borderTopColor: colors.grey[300],
    backgroundColor: colors.grey[50],
    gap: spacing.xs,
  },
  rubricCriterionCell: {
    flex: 3,
  },
  rubricCriterionText: {
    color: colors.grey[700],
  },
  rubricScoreCell: {
    flex: 1.5,
  },
  rubricMaxCell: {
    flex: 1.5,
    color: colors.grey[500],
  },
  rubricInput: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.sm,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: typographyScale.textSm.fontSize,
    textAlign: "center",
    maxWidth: 80,
  },
});

export default ProjectGradingSection;
